import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Stack,
} from "@mui/material";
import API from "../../utils/Axios.utils";
import useAuth from "../../hooks/useAuth";

/**
 * Assign a single recruiter to the selected jobs.
 *
 * The dropdown lists admins of the caller's tenant (org + customer). The value
 * carried around is `recruiter_id`, the Intelliconvo user id, which is what the
 * backend stores on the job — not the TEP user_id.
 */
const AssignRecruiter = ({
  jobIds,
  onSuccess,
  setAlert,
  setAlertType,
  setAlertContent,
}) => {
  const [recruiters, setRecruiters] = useState([]);
  const [selectedRecruiter, setSelectedRecruiter] = useState("");
  const [loading, setLoading] = useState(false);

  const { token } = useAuth();
  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  useEffect(() => {
    if (!jobIds || jobIds.length === 0) return;

    API.get("/api/job/recruiter-list", config)
      .then((res) => {
        setRecruiters(res?.data?.recruiters || []);
      })
      .catch(() => {
        setRecruiters([]);
        setAlertContent("Failed to load recruiters");
        setAlertType("error");
        setAlert(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobIds]);

  const handleSubmit = async () => {
    if (!selectedRecruiter) {
      setAlertContent("Please select a recruiter");
      setAlertType("error");
      setAlert(true);
      return;
    }

    try {
      setLoading(true);

      const res = await API.post(
        "/api/job/assign-recruiter",
        { recruiterId: selectedRecruiter, jobIds },
        config
      );

      // Partial success is normal: jobs the caller does not own are skipped
      // rather than failing the whole batch, so report what actually happened.
      const stats = res?.data?.stats || {};
      const jobsLabel = (n) => `${n} job${n === 1 ? "" : "s"}`;
      const onlyUnchanged = !stats.assigned && stats.unchanged && !stats.skipped && !stats.notFound;

      if (onlyUnchanged) {
        // Re-picking the current recruiter is a no-op, not a failure.
        setAlertContent(
          stats.unchanged === 1
            ? "No change: this job is already assigned to the selected recruiter."
            : `No change: all ${stats.unchanged} jobs are already assigned to the selected recruiter.`
        );
        setAlertType("info");
      } else {
        const parts = [];
        if (stats.assigned) parts.push(`${jobsLabel(stats.assigned)} assigned`);
        if (stats.unchanged) parts.push(`${jobsLabel(stats.unchanged)} already with this recruiter`);
        if (stats.skipped) parts.push(`${jobsLabel(stats.skipped)} skipped (not owner)`);
        if (stats.notFound) parts.push(`${jobsLabel(stats.notFound)} not found`);

        setAlertContent(parts.length ? parts.join(", ") : "No jobs were assigned.");
        setAlertType(stats.assigned ? "success" : "warning");
      }
      setAlert(true);
      setLoading(false);

      onSuccess();
    } catch (error) {
      setLoading(false);
      setAlertContent(
        error?.response?.data?.message || "Something went wrong"
      );
      setAlertType("error");
      setAlert(true);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body2" sx={{ mb: 1, color: "#555" }}>
          Assigning {jobIds?.length || 0} job(s). The recruiter replaces the
          current one and is notified by email.
        </Typography>

        <TextField
          select
          fullWidth
          size="small"
          label="Recruiter"
          value={selectedRecruiter}
          onChange={(e) => setSelectedRecruiter(e.target.value)}
        >
          {recruiters.length === 0 ? (
            <MenuItem value="" disabled>
              No recruiters found
            </MenuItem>
          ) : (
            recruiters.map((rec) => (
              <MenuItem key={rec.recruiter_id} value={rec.recruiter_id}>
                <Stack>
                  <span>{rec.name || rec.email}</span>
                </Stack>
              </MenuItem>
            ))
          )}
        </TextField>
      </Grid>

      <Grid item xs={12} textAlign="center">
        <Button
          variant="contained"
          disabled={!selectedRecruiter || loading}
          onClick={handleSubmit}
          style={{ background: "#2777AA" }}
        >
          {`Assign ${jobIds?.length || 0} Job(s)`}
        </Button>
      </Grid>
    </Grid>
  );
};

export default AssignRecruiter;
